import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import PdfLang from "../components/PdfLang";
import { PageHeader } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { FileDown, Eye, Loader2, Lock } from "lucide-react";

const month = () => { const d = new Date(); return [new Date(d.getFullYear(), d.getMonth(), 1), d].map((x) => x.toISOString().slice(0, 10)); };

export default function PdfReports() {
  const { t, tn, lang } = useI18n();
  const { can, scope, user } = useAuth();
  const to = new Date().toISOString().slice(0, 10);
  const from = (() => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().slice(0, 10); })();
  const tooLong = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1 > 7;
  const [f, setF] = useState({ kind: "reports", project_id: "", team_id: "", employee_ids: [], date_from: from, date_to: to });
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [emps, setEmps] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const canTeam = can("reports.view") && scope("reports.view") !== "self";
  const canMgmt = can("analytics.view");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    if (canTeam) {
      api.get("/employees").then((r) => setEmps(r.data)).catch(() => {});
      api.get("/org/tree").then((r) => setTeams(r.data.teams || [])).catch(() => {});
    }
  }, [canTeam]);

  const [pdfLang, setPdfLang] = useState(lang || "en");
  useEffect(() => { setPdfLang(lang || "en"); }, [lang]);
  const qs = () => new URLSearchParams(Object.entries({ ...f, employee_ids: f.employee_ids.join(","), lang: pdfLang }).filter(([, v]) => v)).toString();
  const doPreview = async () => {
    if (tooLong(f.date_from, f.date_to)) return toast.error(t("max_period_pdf"));
    setBusy("preview");
    try { const { data } = await api.get(`/pdf/preview?${qs()}`); setPreview(data); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" && d.startsWith("MAX_PERIOD") ? t("max_period_pdf") : (d || t("error"))); } finally { setBusy(""); }
  };
  const download = async () => {
    if (tooLong(f.date_from, f.date_to)) return toast.error(t("max_period_pdf"));
    setBusy("pdf");
    try {
      const { data } = await api.get(`/pdf/report?${qs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(data); window.open(url, "_blank");
    } catch (e) { toast.error(t("error")); } finally { setBusy(""); }
  };
  const toggleEmp = (id) => setF((p) => ({ ...p, employee_ids: p.employee_ids.includes(id) ? p.employee_ids.filter((x) => x !== id) : [...p.employee_ids, id] }));
  const teamEmps = f.team_id ? emps.filter((e) => e.team_id === f.team_id) : emps;

  return (
    <div data-testid="pdf-page">
      <PageHeader title={t("pdf_reports")} subtitle={t("pdf_sub")} />
      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        <div className="tactical-card p-4 space-y-4">
          {canMgmt && (
            <div className="flex gap-2">
              {["reports", "management"].map((k) => <button key={k} data-testid={`pdf-kind-${k}`} onClick={() => { setF({ ...f, kind: k }); setPreview(null); }} className={`px-4 h-10 rounded-md text-sm font-semibold ${f.kind === k ? "bg-[var(--sidebar)] text-white" : "tactical-card"}`}>{t(k === "reports" ? "pdf_kind_reports" : "pdf_kind_mgmt")}</button>)}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={t("date_from")}><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="pdf-date-from" className="h-11" /></Field>
            <Field label={t("date_to")}><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="pdf-date-to" className="h-11" /></Field>
            <Field label={t("pdf_lang")}><PdfLang value={pdfLang} onChange={setPdfLang} testid="pdf-page-lang" /></Field>
            <Field label={t("project")}>
              <select value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })} className="border rounded-md h-11 px-2 text-sm w-full bg-[var(--card)]" data-testid="pdf-project">
                <option value="">{t("total")}</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            {canTeam && teams.length > 0 && (
              <Field label={t("team")}>
                <select value={f.team_id} onChange={(e) => setF({ ...f, team_id: e.target.value, employee_ids: [] })} className="border rounded-md h-11 px-2 text-sm w-full bg-[var(--card)]" data-testid="pdf-team">
                  <option value="">{t("total")}</option>{teams.map((tm) => <option key={tm.id} value={tm.id}>{tn(tm.name)}</option>)}
                </select>
              </Field>
            )}
          </div>
          {canTeam && f.kind === "reports" && (
            <Field label={`${t("employees")} (${f.employee_ids.length || t("total")})`}>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="pdf-employees">
                {teamEmps.map((e) => <button key={e.id} data-testid={`pdf-emp-${e.id}`} onClick={() => toggleEmp(e.id)} className={`px-3 h-9 rounded-full text-xs font-semibold ${f.employee_ids.includes(e.id) ? "bg-[var(--amber)] text-white" : "tactical-card"}`}>{e.name}</button>)}
              </div>
            </Field>
          )}
          {!canTeam && <div className="text-sm text-[var(--text-3)]">{user?.name} · {t("scope_self")}</div>}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={doPreview} disabled={!!busy} className="h-11 gap-2 flex-1" data-testid="pdf-preview-btn">{busy === "preview" ? <Loader2 className="animate-spin" size={16} /> : <Eye size={16} />} {t("preview")}</Button>
            <Button onClick={download} disabled={!!busy} className="h-11 gap-2 flex-1 bg-[var(--amber)] text-white" data-testid="pdf-download-btn">{busy === "pdf" ? <Loader2 className="animate-spin" size={16} /> : <FileDown size={16} />} PDF</Button>
          </div>
        </div>
        <div className="tactical-card p-4" data-testid="pdf-preview">
          <div className="text-xs uppercase font-bold text-[var(--text-3)] mb-3">{t("preview")}</div>
          {!preview ? <div className="text-sm text-[var(--text-3)]">{t("preview_hint")}</div> : (
            <div className="space-y-2 text-sm">
              <Row k={t("period")} v={preview.period} />
              <Row k={t("reports")} v={preview.reports} testid="pv-reports" />
              <Row k={t("employees")} v={`${preview.employees}${preview.employees_list?.length ? ` — ${preview.employees_list.join(", ")}` : ""}`} />
              <Row k={t("projects")} v={preview.projects} />
              <Row k={t("items")} v={`${preview.items} · ${preview.quantity}`} />
              <Row k={t("actual_hours")} v={`${preview.actual_hours}h`} />
              <Row k={t("photos")} v={preview.photos} />
              {Object.entries(preview.statuses || {}).map(([s, n]) => <Row key={s} k={t(s)} v={n} small />)}
              <div className="border-t pt-2 mt-2">
                <Row k={t("norm_hours")} v={preview.fields?.efficiency ? `${preview.norm_hours}h · ${preview.efficiency ?? "—"}%` : <Lock size={14} className="inline text-[var(--text-3)]" />} />
                <Row k={t("cost")} v={preview.fields?.finance ? `€${preview.cost}` : <Lock size={14} className="inline text-[var(--text-3)]" />} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
const Field = ({ label, children }) => <div><label className="text-xs uppercase font-semibold text-[var(--text-3)] mb-1 block">{label}</label>{children}</div>;
const Row = ({ k, v, small, testid }) => <div className={`flex justify-between gap-3 ${small ? "text-xs text-[var(--text-3)]" : ""}`}><span className="text-[var(--text-3)]">{k}</span><span className="font-semibold text-right" data-testid={testid}>{v}</span></div>;
